import { HTMLAttributes, useCallback, useEffect, useMemo, useState } from 'react'
import { useMedia } from 'react-use'
import useEmblaCarousel from 'embla-carousel-react'
import Image from 'next/image'

import Button from '@/components/base/button'
import Container from '@/components/base/container'
import Section from '@/components/base/section'
import SVGIcon from '@/components/base/svg-icon'
import Typography from '@/components/base/typography'
import { useStore } from '@/contexts/store'

import { useImageTextCarouselMotion } from './motion'
import { cn } from '@/lib/utils'
import { WPImage } from '@/types'

type ImageTextCarouselProps = HTMLAttributes<HTMLDivElement> & {
  items: {
    pretitle: string
    title: string
    text: string
    image: WPImage
  }[]
  className?: string
}

const ImageTextCarousel = ({ items, className, ...props }: ImageTextCarouselProps) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    containScroll: 'keepSnaps',
    loop: true
  })

  const [selectedIndex, setSelectedIndex] = useState(0)
  const [scrollSnaps, setScrollSnaps] = useState<number[]>([])
  const needsCarousel = useMemo(() => items.length > 1, [items.length])
  const isMobile = useMedia('(max-width: 767px)', false)
  const { motionRef } = useImageTextCarouselMotion()

  const onInit = useCallback(() => {
    if (!emblaApi) return
    setScrollSnaps(emblaApi.scrollSnapList())
  }, [emblaApi])

  const onSelect = useCallback(() => {
    if (!emblaApi) return
    setSelectedIndex(emblaApi.selectedScrollSnap())
  }, [emblaApi])

  const onDotButtonClick = useCallback(
    (index: number) => {
      if (!emblaApi) return
      emblaApi.scrollTo(index)
    },
    [emblaApi]
  )

  useEffect(() => {
    if (!emblaApi) return
    emblaApi.reInit({ active: needsCarousel })
  }, [needsCarousel, emblaApi])

  useEffect(() => {
    if (!emblaApi) return

    onInit()
    onSelect()
    emblaApi.on('reInit', onInit).on('reInit', onSelect).on('select', onSelect)
  }, [emblaApi, onInit, onSelect])

  const { strings } = useStore()

  return (
    <Section
      ref={motionRef}
      className={cn('text-text-accent-light dark:text-text-accent-light', className)}
      data-image-text-carousel
      {...props}
    >
      <Container className="relative overflow-clip" data-motion-block>
        <div ref={emblaRef} className="md:overflow-hidden md:rounded-radius-minimal">
          <div className="-ml-margin flex touch-pan-y md:ml-0">
            {items?.map((item, index) => (
              <div
                key={`item-${index}`}
                className={cn('min-w-0 shrink-0 grow-0 basis-full pl-margin md:pl-0')}
              >
                <div
                  className={cn(
                    'relative overflow-clip',
                    'aspect-[2/3] md:aspect-[2/1.5] lg:aspect-video',
                    'rounded-radius-minimal md:rounded-none'
                  )}
                >
                  <div className="pointer-events-none absolute bottom-[9.5rem] left-0 z-20 flex w-full flex-col gap-spacing-sm p-spacing-sm md:w-[70%] md:p-spacing-lg lg:w-[50%]">
                    <Typography variant="body">{item.pretitle}</Typography>
                    <Typography variant="h6" className="font-normal">
                      {item.title}
                    </Typography>
                    <Typography variant="body">{item.text}</Typography>
                  </div>
                  <div className="z-1 absolute inset-0">
                    <div className="absolute inset-0 z-10 bg-[linear-gradient(0deg,_#ffffffaa_0%,_#ffffff40_50%)]" />
                    <Image
                      src={item.image.url}
                      alt={item.image.alt || ''}
                      width={item.image.width || 600}
                      height={item.image.height || 400}
                      className="h-full w-full object-cover"
                      priority
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>

          {scrollSnaps.length > 1 && !isMobile ? (
            <div className="absolute bottom-0 left-margin flex space-x-[0.4rem] p-spacing-lg">
              <Button
                type="button"
                onClick={() => emblaApi && emblaApi.scrollPrev()}
                aria-label="Previous"
                startIcon={
                  <SVGIcon
                    symbol="prev-arrow-icon"
                    className="h-[0.7rem] w-[2.4rem] fill-current"
                  />
                }
              >
                <Typography variant="body" mask>
                  {strings?.labels?.prev}
                </Typography>
              </Button>
              <Button
                type="button"
                onClick={() => emblaApi && emblaApi.scrollNext()}
                aria-label="Next"
                endIcon={
                  <SVGIcon
                    symbol="next-arrow-icon"
                    className="h-[0.7rem] w-[2.4rem] fill-current"
                  />
                }
              >
                <Typography variant="body" mask>
                  {strings?.labels?.next}
                </Typography>
              </Button>
            </div>
          ) : null}
        </div>

        {scrollSnaps.length > 1 && isMobile ? (
          <div className="relative col-span-4 h-[.8rem] pt-spacing-md">
            <div
              className={
                'absolute bottom-0 left-[calc(50%-.4rem)] flex items-center justify-center gap-spacing-2xs transition-all duration-300 ease-out-cubic'
              }
              style={{
                transform: `translateX(-${0.8 * 2 * selectedIndex}rem)`
              }}
            >
              {scrollSnaps.map((_, index) => (
                <div key={index} className="flex items-center justify-center">
                  <button
                    className="pointer-events-all -m-[.3rem] flex p-[.3rem]"
                    onClick={() => onDotButtonClick(index)}
                  >
                    <span className="sr-only">{`Slide ${index}`}</span>
                    <span
                      className={cn(
                        'relative',
                        'transition-all duration-300 ease-out-cubic',
                        'mx-[0rem] h-[0.8rem] w-[0.8rem] scale-0 rounded-full bg-text-accent-light dark:bg-white-light',
                        {
                          'scale-[1]': index === selectedIndex,
                          'scale-[0.6]': index === selectedIndex - 1 || index === selectedIndex + 1,
                          'scale-[0.3]': index === selectedIndex - 2 || index === selectedIndex + 2
                        }
                      )}
                    />
                  </button>
                </div>
              ))}
            </div>
          </div>
        ) : null}
      </Container>
    </Section>
  )
}

export default ImageTextCarousel
